/*
  window.underneatSizeChart — open any product's size chart from any page.

    hasChart(productId) -> Promise<boolean>
    open(productId)     -> Promise<boolean>

  Current product: uses the page's own modal (window.openCustomSizeChart), or the
  "Find Your Fit" button for bras without a chart.
  Any other product: fetches its page and injects its #quickbuy-size-chart-modal-content
  snippet (markup + styles + script), exactly like quickbuy.js does, then calls
  window.openCustomSizeChartQuickBuy. Shares the same element/style IDs as quickbuy.js
  so the two replace each other instead of stacking. quickbuy.js is intentionally untouched.
*/
(function () {
  if (window.underneatSizeChart) return;

  const QB_MODAL_ID = "quickbuy-size-chart-modal-content";
  const QB_STYLE_ID = "quickbuy-size-chart-styles"; // same ID quickbuy.js uses
  const CATALOG_PAGE_LIMIT = 250;

  const entries = new Map(); // productId -> Promise<{html, style, script} | null>
  let catalogPromise = null;

  function currentProductId() {
    const script = document.querySelector('script[type="application/json"][id^="ProductJson-"]');
    if (!script) return null;
    try {
      return String(JSON.parse(script.textContent).id);
    } catch (e) {
      return null;
    }
  }

  function findProductUrlInDom(id) {
    const scope = document.querySelector('[data-product-id="' + id + '"]');
    if (!scope) return null;
    if (scope.tagName === "A" && scope.getAttribute("href")) return scope.getAttribute("href");
    const link = scope.querySelector('a[href*="/products/"]') || scope.closest('a[href*="/products/"]');
    return link ? link.getAttribute("href") : null;
  }

  // ponytail: loads the whole catalog index once (1 request per 250 products); fine for this store's size.
  function loadCatalog() {
    if (catalogPromise) return catalogPromise;
    const index = new Map();
    const fetchPage = (page) =>
      fetch("/products.json?limit=" + CATALOG_PAGE_LIMIT + "&page=" + page, { credentials: "same-origin" })
        .then((res) => {
          if (!res.ok) throw new Error("products.json " + res.status);
          return res.json();
        })
        .then((data) => {
          const products = (data && data.products) || [];
          products.forEach((p) => {
            if (p && p.id != null && p.handle) index.set(String(p.id), "/products/" + p.handle);
          });
          return products.length < CATALOG_PAGE_LIMIT ? index : fetchPage(page + 1);
        });
    catalogPromise = fetchPage(1).catch(() => {
      catalogPromise = null; // transient failure: allow a retry on the next call
      return index;
    });
    return catalogPromise;
  }

  function resolveProductUrl(id) {
    const domUrl = findProductUrlInDom(id);
    if (domUrl) return Promise.resolve(domUrl);
    return loadCatalog().then((index) => index.get(id) || null);
  }

  // Same extraction quickbuy.js does for its 'custom-quickbuy' size chart type.
  function extractEntry(doc) {
    const modal = doc.querySelector("#" + QB_MODAL_ID);
    if (!modal) return null;

    let style = "";
    doc.querySelectorAll("style").forEach((tag) => {
      const t = tag.textContent || "";
      if (t.includes("size-chart-")) style += t + "\n";
    });

    let script = "";
    doc.querySelectorAll("script").forEach((tag) => {
      const t = tag.textContent || "";
      if (t.includes("openCustomSizeChartQuickBuy")) script = t;
    });

    return { html: modal.outerHTML, style, script };
  }

  function loadEntry(id) {
    if (entries.has(id)) return entries.get(id);
    const promise = resolveProductUrl(id)
      .then((url) => {
        if (!url) return null;
        return fetch(url, { credentials: "same-origin" })
          .then((res) => {
            if (!res.ok) throw new Error("Failed to fetch " + url);
            return res.text();
          })
          .then((html) => extractEntry(new DOMParser().parseFromString(html, "text/html")));
      })
      .catch(() => {
        entries.delete(id); // network error: don't cache, allow a retry
        return null;
      });
    entries.set(id, promise);
    return promise;
  }

  // Mirrors quickbuy.js populateSizeChartModal() for 'custom-quickbuy'.
  function inject(id, entry) {
    const existing = document.getElementById(QB_MODAL_ID);
    // Already showing this product's chart (and Quick Buy hasn't replaced it since).
    if (existing && existing.getAttribute("data-underneat-product-id") === id) return;
    if (existing) existing.remove();

    const oldStyle = document.getElementById(QB_STYLE_ID);
    if (oldStyle) oldStyle.remove();
    if (entry.style) {
      const styleEl = document.createElement("style");
      styleEl.id = QB_STYLE_ID;
      styleEl.textContent = entry.style;
      document.head.appendChild(styleEl);
    }

    const temp = document.createElement("div");
    temp.innerHTML = entry.html;
    const modal = temp.querySelector("#" + QB_MODAL_ID);
    if (!modal) return;
    modal.setAttribute("data-underneat-product-id", id);
    document.body.appendChild(modal);

    // The snippet's own script defines openCustomSizeChartQuickBuy + close/unit-toggle handling.
    if (entry.script) {
      const scriptEl = document.createElement("script");
      scriptEl.textContent = entry.script;
      document.body.appendChild(scriptEl);
    }
  }

  function currentPageTarget() {
    if (document.getElementById("custom-product-size-chart-modal") && typeof window.openCustomSizeChart === "function") {
      return () => window.openCustomSizeChart();
    }
    const braBtn = document.querySelector(".bra-find-fit-btn");
    if (braBtn) return () => braBtn.click();
    return null;
  }

  window.underneatSizeChart = {
    hasChart(productId) {
      const id = String(productId);
      if (id === currentProductId()) return Promise.resolve(!!currentPageTarget());
      return loadEntry(id).then((entry) => !!entry);
    },

    open(productId) {
      const id = String(productId);
      if (id === currentProductId()) {
        const target = currentPageTarget();
        if (target) target();
        return Promise.resolve(!!target);
      }
      return loadEntry(id).then((entry) => {
        if (!entry) return false;
        inject(id, entry);
        if (typeof window.openCustomSizeChartQuickBuy !== "function") return false;
        window.openCustomSizeChartQuickBuy();
        return true;
      });
    },
  };
})();
